import { TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import {
  StyledGroupTableRow,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  StyledTableSubtitle,
  TitleWrapper,
} from './Table.styles';
import { TableHeadCell } from './Table.styles';
import InfoIcon from '@mui/icons-material/Info';
import { Stack } from '@mui/material/';
import { useTranslation } from 'react-i18next';

export interface IColumn {
  id: string;
  name: string;
  width?: string;
  iconDescription?: string;
}

interface ITableComponent {
  tableName?: string;
  rows: Record<string, unknown>[];
  columns: IColumn[];
  isHead?: boolean;
  dataTestId?: string;
}

const InitiativesTableBody = ({ rows, columns }: ITableComponent) => {
  const { t } = useTranslation();
  const marketingInitiatives = rows?.filter((row) => row.resource === 'marketing');
  const educationInitiatives = rows?.filter((row) => row.resource === 'education');
  const careResources = rows?.filter((row) => row.resource === 'care');
  return (
    <StyledTableBody>
      {!!marketingInitiatives.length && (
        <>
          <StyledTableSubtitle>{t('marketingResources')}</StyledTableSubtitle>
          {marketingInitiatives.map((row) => {
            return (
              <StyledTableRow key={(row.id || row.kpi) as string}>
                {columns.map((cell) => (
                  <StyledTableCell
                    key={row.id + cell.id}
                    component="th"
                    scope="row"
                    styledBgColor={!!row.intlBenchmarkKpi && cell.id === 'globalBenchmarkKpi'}
                  >
                    {row[cell.id] as string}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            );
          })}
        </>
      )}
      {!!educationInitiatives.length && (
        <>
          <StyledTableSubtitle>{t('educationResources')}</StyledTableSubtitle>
          {educationInitiatives.map((row) => {
            return (
              <StyledTableRow key={(row.id || row.kpi) as string}>
                {columns.map((cell) => (
                  <StyledTableCell
                    key={row.id + cell.id}
                    component="th"
                    scope="row"
                    styledBgColor={!!row.intlBenchmarkKpi && cell.id === 'globalBenchmarkKpi'}
                  >
                    {row[cell.id] as string}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            );
          })}
        </>
      )}
      {!!careResources.length && (
        <>
          <StyledTableSubtitle>{t('careResources')}</StyledTableSubtitle>
          {careResources.map((row) => {
            return (
              <StyledTableRow key={(row.id || row.kpi) as string}>
                {columns.map((cell) => (
                  <StyledTableCell
                    key={row.id + cell.id}
                    component="th"
                    scope="row"
                    styledBgColor={!!row.intlBenchmarkKpi && cell.id === 'globalBenchmarkKpi'}
                  >
                    {row[cell.id] as string}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            );
          })}
        </>
      )}
    </StyledTableBody>
  );
};

export const TableComponent = ({
  tableName,
  rows,
  columns,
  isHead = true,
  dataTestId,
}: ITableComponent) => {
  return (
    <>
      {tableName && (
        <TitleWrapper>
          <Typography variant="h6" textTransform="capitalize">
            {tableName}
          </Typography>
        </TitleWrapper>
      )}
      <StyledTableContainer data-testid={dataTestId || tableName}>
        <StyledTable>
          {isHead && (
            <StyledTableHead>
              <TableRow>
                {columns?.map((cell) => (
                  <TableHeadCell
                    key={cell.id}
                    width={cell.width}
                    styledBgColor={columns.length > 4 && cell.id === 'globalBenchmarkKpi'}
                  >
                    <Stack>
                      {cell.name}
                      {cell.iconDescription && (
                        <Tooltip title={cell.iconDescription} arrow placement={'top'}>
                          <InfoIcon />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableHeadCell>
                ))}
              </TableRow>
            </StyledTableHead>
          )}
          {dataTestId === 'initiatives-table' ? (
            <InitiativesTableBody rows={rows} columns={columns} />
          ) : (
            <StyledTableBody>
              {rows?.map((row) => {
                return row.groupRow ? (
                  <StyledGroupTableRow key={row.id as string}>
                    <TableCell colSpan={5}>{row.title as string}</TableCell>
                  </StyledGroupTableRow>
                ) : (
                  <StyledTableRow key={(row.id || row.kpi) as string}>
                    {columns.map((cell) => (
                      <StyledTableCell
                        key={row.id + cell.id}
                        component="th"
                        scope="row"
                        styledBgColor={!!row.intlBenchmarkKpi && cell.id === 'globalBenchmarkKpi'}
                      >
                        {row[cell.id] as string}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                );
              })}
            </StyledTableBody>
          )}
        </StyledTable>
      </StyledTableContainer>
    </>
  );
};
